import {
  cloneEvent,
  createDefaultProject,
  createEmptyPattern,
  createInstrumentTemplate,
  isTrackerControlNote,
  sanitizeEffectArg,
  sanitizeInstrumentIndex,
  sanitizeOrderList,
  sanitizePatternIndex,
  structuredCloneProject,
  TRACKER_CHANNEL_COUNT,
  TRACKER_INSTRUMENT_EMPTY,
  TRACKER_MAX_ORDERS,
  TRACKER_NOTE_COUNT,
  TRACKER_NOTE_EMPTY,
  TRACKER_ROWS_PER_PATTERN,
  type ChannelEvent,
  type TrackerEffectCode,
  type TrackerInstrument,
  type TrackerProject
} from "./model.js";

export type TrackerSelection = {
  anchorChannel: number;
  anchorRow: number;
  focusChannel: number;
  focusRow: number;
};

export type TrackerClipboard = {
  cells: ChannelEvent[][];
  height: number;
  width: number;
};

export type TrackerPlaybackState = "idle" | "written" | "playing" | "stopped" | "error" | "auditioning";

export type TrackerPlaybackUi = {
  audioDiagnostics: string;
  audioStatus: string;
  bytecodeSize: number;
  errorCode: number;
  lastError: string | null;
  lastMessage: string;
  order: number;
  ownedByTracker: boolean;
  pattern: number;
  row: number;
  state: TrackerPlaybackState;
  tick: number;
  workspaceWritten: boolean;
};

export type TrackerUiState = {
  activePattern: number;
  clipboard: TrackerClipboard | null;
  currentOctave: number;
  editStep: number;
  followRow: boolean;
  loopEnabled: boolean;
  muteMask: number;
  playback: TrackerPlaybackUi;
  selectedOrderSlot: number;
  selectedInstrument: number;
  selection: TrackerSelection;
  soloMask: number;
};

export type TrackerState = {
  canRedo: boolean;
  canUndo: boolean;
  lastRedoLabel: string | null;
  lastUndoLabel: string | null;
  projectRevision: number;
  project: TrackerProject;
  redoDepth: number;
  undoDepth: number;
  ui: TrackerUiState;
};

type ProjectSnapshot = {
  label: string;
  project: TrackerProject;
  ui: Pick<TrackerUiState, "activePattern" | "selectedInstrument" | "selectedOrderSlot" | "selection">;
};

type Listener = (state: TrackerState) => void;

function clampRow(value: number): number {
  return Math.max(0, Math.min(TRACKER_ROWS_PER_PATTERN - 1, Math.round(value)));
}

function clampChannel(value: number): number {
  return Math.max(0, Math.min(TRACKER_CHANNEL_COUNT - 1, Math.round(value)));
}

function clampOrderSlot(value: number, orderCount: number): number {
  if (orderCount <= 0) {
    return 0;
  }
  return Math.max(0, Math.min(orderCount - 1, Math.round(value)));
}

function findClosestOrderSlot(orderList: number[], pattern: number, preferredSlot: number): number {
  if (orderList.length === 0) {
    return 0;
  }
  const preferred = clampOrderSlot(preferredSlot, orderList.length);
  if (orderList[preferred] === pattern) {
    return preferred;
  }
  let best = -1;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (let index = 0; index < orderList.length; index += 1) {
    if (orderList[index] !== pattern) {
      continue;
    }
    const distance = Math.abs(index - preferred);
    if (distance < bestDistance) {
      best = index;
      bestDistance = distance;
    }
  }
  return best >= 0 ? best : preferred;
}

function normalizeSelection(selection: TrackerSelection): TrackerSelection {
  return {
    anchorChannel: clampChannel(selection.anchorChannel),
    anchorRow: clampRow(selection.anchorRow),
    focusChannel: clampChannel(selection.focusChannel),
    focusRow: clampRow(selection.focusRow)
  };
}

function emptyPlayback(): TrackerPlaybackUi {
  return {
    audioDiagnostics: "Queue 0 fr | Peak 0 fr | Underruns 0 | Dropped 0 | Emulator backlog 0 fr",
    audioStatus: "Audio preview uses the main emulator output.",
    bytecodeSize: 0,
    errorCode: 0,
    lastError: null,
    lastMessage: "Tracker ready.",
    order: 0,
    ownedByTracker: false,
    pattern: 0,
    row: 0,
    state: "idle",
    tick: 0,
    workspaceWritten: false
  };
}

function cloneClipboard(clipboard: TrackerClipboard | null): TrackerClipboard | null {
  if (clipboard === null) {
    return null;
  }
  return {
    width: clipboard.width,
    height: clipboard.height,
    cells: clipboard.cells.map((row) => row.map((cell) => cloneEvent(cell)))
  };
}

function selectionBounds(selection: TrackerSelection): {
  startChannel: number;
  endChannel: number;
  startRow: number;
  endRow: number;
} {
  return {
    startChannel: Math.min(selection.anchorChannel, selection.focusChannel),
    endChannel: Math.max(selection.anchorChannel, selection.focusChannel),
    startRow: Math.min(selection.anchorRow, selection.focusRow),
    endRow: Math.max(selection.anchorRow, selection.focusRow)
  };
}

export class TrackerStore {
  private project: TrackerProject;
  private projectRevision = 0;
  private readonly listeners = new Set<Listener>();
  private readonly undoStack: ProjectSnapshot[] = [];
  private readonly redoStack: ProjectSnapshot[] = [];
  private ui: TrackerUiState;

  constructor(project = createDefaultProject()) {
    this.project = structuredCloneProject(project);
    this.ui = {
      activePattern: 0,
      clipboard: null,
      currentOctave: 4,
      editStep: 1,
      followRow: true,
      loopEnabled: true,
      muteMask: 0,
      playback: emptyPlayback(),
      selectedOrderSlot: 0,
      selectedInstrument: 0,
      selection: { anchorChannel: 0, anchorRow: 0, focusChannel: 0, focusRow: 0 },
      soloMask: 0
    };
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    listener(this.getState());
    return () => {
      this.listeners.delete(listener);
    };
  }

  getState(): TrackerState {
    const lastRedo = this.redoStack.length > 0 ? this.redoStack[this.redoStack.length - 1] : null;
    const lastUndo = this.undoStack.length > 0 ? this.undoStack[this.undoStack.length - 1] : null;
    return {
      canRedo: this.redoStack.length > 0,
      canUndo: this.undoStack.length > 0,
      lastRedoLabel: lastRedo?.label ?? null,
      lastUndoLabel: lastUndo?.label ?? null,
      projectRevision: this.projectRevision,
      project: this.project,
      redoDepth: this.redoStack.length,
      undoDepth: this.undoStack.length,
      ui: {
        ...this.ui,
        clipboard: cloneClipboard(this.ui.clipboard),
        playback: { ...this.ui.playback },
        selection: { ...this.ui.selection }
      }
    };
  }

  private emit(): void {
    const state = this.getState();
    for (const listener of this.listeners) {
      listener(state);
    }
  }

  private pushUndoSnapshot(label: string): void {
    this.undoStack.push({
      label,
      project: structuredCloneProject(this.project),
      ui: {
        activePattern: this.ui.activePattern,
        selectedOrderSlot: this.ui.selectedOrderSlot,
        selectedInstrument: this.ui.selectedInstrument,
        selection: { ...this.ui.selection }
      }
    });
    if (this.undoStack.length > 64) {
      this.undoStack.shift();
    }
    this.redoStack.length = 0;
  }

  private restoreSnapshot(snapshot: ProjectSnapshot): void {
    this.project = structuredCloneProject(snapshot.project);
    this.ui.activePattern = sanitizePatternIndex(snapshot.ui.activePattern, this.project.song.patterns.length);
    this.ui.selectedOrderSlot = clampOrderSlot(snapshot.ui.selectedOrderSlot, this.project.song.orderList.length);
    this.ui.selectedInstrument = sanitizeInstrumentIndex(snapshot.ui.selectedInstrument, this.project.song.instruments.length);
    this.ui.selection = normalizeSelection(snapshot.ui.selection);
  }

  private syncSelectedOrderSlotToActivePattern(preferredSlot = this.ui.selectedOrderSlot): void {
    this.ui.selectedOrderSlot = findClosestOrderSlot(this.project.song.orderList, this.ui.activePattern, preferredSlot);
  }

  private mutateProject(label: string, mutator: () => void): void {
    this.pushUndoSnapshot(label);
    mutator();
    this.projectRevision += 1;
    this.ui.activePattern = sanitizePatternIndex(this.ui.activePattern, this.project.song.patterns.length);
    this.ui.selectedOrderSlot = clampOrderSlot(this.ui.selectedOrderSlot, this.project.song.orderList.length);
    this.ui.selectedInstrument = sanitizeInstrumentIndex(this.ui.selectedInstrument, this.project.song.instruments.length);
    this.ui.selection = normalizeSelection(this.ui.selection);
    this.emit();
  }

  private currentPattern() {
    return this.project.song.patterns[this.ui.activePattern];
  }

  private currentEvent(): ChannelEvent {
    return this.currentPattern().rows[this.ui.selection.focusRow][this.ui.selection.focusChannel];
  }

  setProject(project: TrackerProject): void {
    this.project = structuredCloneProject(project);
    this.projectRevision += 1;
    this.undoStack.length = 0;
    this.redoStack.length = 0;
    this.ui.activePattern = 0;
    this.ui.clipboard = null;
    this.ui.followRow = true;
    this.ui.muteMask = 0;
    this.ui.playback = emptyPlayback();
    this.ui.selectedInstrument = 0;
    this.ui.selectedOrderSlot = 0;
    this.ui.selection = { anchorChannel: 0, anchorRow: 0, focusChannel: 0, focusRow: 0 };
    this.ui.soloMask = 0;
    this.ui.activePattern = sanitizePatternIndex(this.ui.activePattern, this.project.song.patterns.length);
    this.syncSelectedOrderSlotToActivePattern(0);
    this.ui.selectedInstrument = sanitizeInstrumentIndex(this.ui.selectedInstrument, this.project.song.instruments.length);
    this.ui.selection = normalizeSelection(this.ui.selection);
    this.emit();
  }

  resetProject(): void {
    this.setProject(createDefaultProject());
  }

  updateTitle(title: string): void {
    this.mutateProject("Rename song", () => {
      this.project.title = title.trim() === "" ? "Untitled Tracker Song" : title;
    });
  }

  updateSpeed(speed: number): void {
    this.mutateProject("Change speed", () => {
      this.project.song.speed = Math.max(1, Math.min(31, Math.round(speed)));
    });
  }

  updateTempo(tempo: number): void {
    this.mutateProject("Change tempo", () => {
      this.project.song.tempo = Math.max(32, Math.min(255, Math.round(tempo)));
    });
  }

  updatePatternName(name: string): void {
    this.mutateProject("Rename pattern", () => {
      this.project.song.patterns[this.ui.activePattern].name =
        name.trim() === "" ? `Pattern ${this.ui.activePattern.toString().padStart(2, "0")}` : name;
    });
  }

  updateGlobals(partial: Partial<TrackerProject["song"]["globals"]>): void {
    this.mutateProject("Edit globals", () => {
      this.project.song.globals = {
        ...this.project.song.globals,
        ...partial
      };
    });
  }

  setActivePattern(index: number): void {
    this.ui.activePattern = sanitizePatternIndex(index, this.project.song.patterns.length);
    this.syncSelectedOrderSlotToActivePattern();
    this.emit();
  }

  setSelectedOrderSlot(index: number): void {
    this.ui.selectedOrderSlot = clampOrderSlot(index, this.project.song.orderList.length);
    this.ui.activePattern = sanitizePatternIndex(this.project.song.orderList[this.ui.selectedOrderSlot] ?? this.ui.activePattern, this.project.song.patterns.length);
    this.emit();
  }

  addPattern(): void {
    this.mutateProject("Add pattern", () => {
      const nextIndex = this.project.song.patterns.length;
      this.project.song.patterns.push(createEmptyPattern(`Pattern ${nextIndex.toString().padStart(2, "0")}`));
      this.ui.activePattern = nextIndex;
      this.project.song.orderList.push(nextIndex);
      this.project.song.orderList = sanitizeOrderList(this.project.song.orderList, this.project.song.patterns.length).slice(0, TRACKER_MAX_ORDERS);
      this.ui.selectedOrderSlot = this.project.song.orderList.length - 1;
    });
  }

  deleteActivePattern(): void {
    if (this.project.song.patterns.length <= 1) {
      return;
    }
    this.mutateProject("Delete pattern", () => {
      const deletedPattern = this.ui.activePattern;
      this.project.song.patterns.splice(deletedPattern, 1);
      const remappedOrderList = this.project.song.orderList
        .filter((patternIndex) => patternIndex !== deletedPattern)
        .map((patternIndex) => (patternIndex > deletedPattern ? patternIndex - 1 : patternIndex));
      this.project.song.orderList = remappedOrderList.length > 0 ? remappedOrderList : [0];
      this.ui.activePattern = Math.min(deletedPattern, this.project.song.patterns.length - 1);
      this.syncSelectedOrderSlotToActivePattern(this.ui.selectedOrderSlot);
    });
  }

  duplicateActivePattern(): void {
    this.mutateProject("Duplicate pattern", () => {
      const sourceIndex = this.ui.activePattern;
      const source = this.project.song.patterns[sourceIndex];
      const clone = createEmptyPattern(`Pattern ${this.project.song.patterns.length.toString().padStart(2, "0")}`);
      clone.rows = source.rows.map((row) => row.map((cell) => cloneEvent(cell)));
      this.project.song.patterns.push(clone);
      this.ui.activePattern = this.project.song.patterns.length - 1;
      this.project.song.orderList.push(this.ui.activePattern);
      this.project.song.orderList = sanitizeOrderList(this.project.song.orderList, this.project.song.patterns.length).slice(0, TRACKER_MAX_ORDERS);
      this.ui.selectedOrderSlot = this.project.song.orderList.length - 1;
    });
  }

  updateOrderList(values: number[]): void {
    this.mutateProject("Edit order list", () => {
      this.project.song.orderList = sanitizeOrderList(values, this.project.song.patterns.length);
      this.syncSelectedOrderSlotToActivePattern();
    });
  }

  reorderOrderSlot(fromIndex: number, toIndex: number): void {
    if (fromIndex === toIndex) {
      return;
    }
    this.mutateProject("Reorder order strip", () => {
      const from = clampOrderSlot(fromIndex, this.project.song.orderList.length);
      const to = clampOrderSlot(toIndex, this.project.song.orderList.length);
      if (from === to) {
        return;
      }
      const [moved] = this.project.song.orderList.splice(from, 1);
      this.project.song.orderList.splice(to, 0, moved ?? 0);
      if (this.ui.selectedOrderSlot === from) {
        this.ui.selectedOrderSlot = to;
      } else if (from < this.ui.selectedOrderSlot && to >= this.ui.selectedOrderSlot) {
        this.ui.selectedOrderSlot -= 1;
      } else if (from > this.ui.selectedOrderSlot && to <= this.ui.selectedOrderSlot) {
        this.ui.selectedOrderSlot += 1;
      }
      this.syncSelectedOrderSlotToActivePattern(this.ui.selectedOrderSlot);
    });
  }

  setSelection(row: number, channel: number, extend = false): void {
    const next = {
      ...this.ui.selection,
      focusRow: clampRow(row),
      focusChannel: clampChannel(channel)
    };
    if (!extend) {
      next.anchorRow = next.focusRow;
      next.anchorChannel = next.focusChannel;
    }
    this.ui.selection = normalizeSelection(next);
    this.emit();
  }

  moveSelection(deltaRow: number, deltaChannel: number, extend = false): void {
    this.setSelection(this.ui.selection.focusRow + deltaRow, this.ui.selection.focusChannel + deltaChannel, extend);
  }

  setSelectedInstrument(index: number): void {
    this.ui.selectedInstrument = sanitizeInstrumentIndex(index, this.project.song.instruments.length);
    this.emit();
  }

  renameSelectedInstrument(name: string): void {
    this.mutateProject("Rename instrument", () => {
      this.project.song.instruments[this.ui.selectedInstrument].name =
        name.trim() === "" ? `Instrument ${(this.ui.selectedInstrument + 1).toString().padStart(2, "0")}` : name;
    });
  }

  duplicateSelectedInstrument(): void {
    this.mutateProject("Duplicate instrument", () => {
      const instrument = this.project.song.instruments[this.ui.selectedInstrument];
      const copy: TrackerInstrument = { ...instrument, name: `${instrument.name} Copy` };
      this.project.song.instruments.splice(this.ui.selectedInstrument + 1, 0, copy);
      this.ui.selectedInstrument += 1;
    });
  }

  addInstrument(): void {
    this.mutateProject("Add instrument", () => {
      const nextIndex = this.project.song.instruments.length;
      this.project.song.instruments.push(createInstrumentTemplate(nextIndex));
      this.ui.selectedInstrument = nextIndex;
    });
  }

  deleteSelectedInstrument(): void {
    if (this.project.song.instruments.length <= 1) {
      return;
    }
    this.mutateProject("Delete instrument", () => {
      const deletedInstrument = this.ui.selectedInstrument;
      this.project.song.instruments.splice(deletedInstrument, 1);
      for (const pattern of this.project.song.patterns) {
        for (const row of pattern.rows) {
          for (const event of row) {
            if (event.instrument === deletedInstrument) {
              event.instrument = TRACKER_INSTRUMENT_EMPTY;
            } else if (event.instrument !== TRACKER_INSTRUMENT_EMPTY && event.instrument > deletedInstrument) {
              event.instrument -= 1;
            }
          }
        }
      }
      this.ui.selectedInstrument = Math.min(deletedInstrument, this.project.song.instruments.length - 1);
    });
  }

  updateSelectedInstrument(partial: Partial<TrackerInstrument>): void {
    this.mutateProject("Edit instrument", () => {
      this.project.song.instruments[this.ui.selectedInstrument] = {
        ...this.project.song.instruments[this.ui.selectedInstrument],
        ...partial
      };
    });
  }

  updateCurrentEvent(partial: Partial<ChannelEvent>): void {
    this.mutateProject("Edit cell", () => {
      const event = this.currentEvent();
      event.note = partial.note ?? event.note;
      event.instrument = partial.instrument ?? event.instrument;
      event.effect = (partial.effect ?? event.effect) as TrackerEffectCode;
      event.effectArg = partial.effectArg ?? event.effectArg;
    });
  }

  setCurrentEventNote(note: number): void {
    this.mutateProject("Change note", () => {
      const event = this.currentEvent();
      event.note = note === TRACKER_NOTE_EMPTY || isTrackerControlNote(note) ? note : clampRow(0) + note;
    });
  }

  setCurrentEventInstrument(instrument: number): void {
    this.mutateProject("Change instrument", () => {
      this.currentEvent().instrument = instrument;
    });
  }

  setCurrentEventEffect(effect: TrackerEffectCode): void {
    this.mutateProject("Change effect", () => {
      this.currentEvent().effect = effect;
    });
  }

  setCurrentEventEffectArg(effectArg: number): void {
    this.mutateProject("Change effect arg", () => {
      this.currentEvent().effectArg = sanitizeEffectArg(effectArg);
    });
  }

  applyNoteInput(note: number): void {
    this.mutateProject("Enter note", () => {
      const event = this.currentEvent();
      event.note = note;
      if (note !== TRACKER_NOTE_EMPTY && !isTrackerControlNote(note)) {
        event.instrument = this.ui.selectedInstrument;
      }
    });
    this.moveSelection(this.ui.editStep, 0);
  }

  setCurrentOctave(octave: number): void {
    this.ui.currentOctave = Math.max(2, Math.min(6, Math.round(octave)));
    this.emit();
  }

  setEditStep(step: number): void {
    this.ui.editStep = Math.max(1, Math.min(16, Math.round(step)));
    this.emit();
  }

  setLoopEnabled(enabled: boolean): void {
    this.ui.loopEnabled = enabled;
    this.emit();
  }

  setFollowRow(enabled: boolean): void {
    this.ui.followRow = enabled;
    this.emit();
  }

  toggleMute(channel: number): void {
    const mask = 1 << clampChannel(channel);
    const willEnableMute = (this.ui.muteMask & mask) === 0;
    this.ui.muteMask ^= mask;
    if (willEnableMute) {
      this.ui.soloMask &= ~mask;
    }
    this.emit();
  }

  toggleSolo(channel: number): void {
    const mask = 1 << clampChannel(channel);
    const willEnableSolo = (this.ui.soloMask & mask) === 0;
    this.ui.soloMask ^= mask;
    if (willEnableSolo) {
      this.ui.muteMask &= ~mask;
    }
    this.emit();
  }

  copySelection(): TrackerClipboard {
    const bounds = selectionBounds(this.ui.selection);
    const cells = [] as ChannelEvent[][];
    for (let row = bounds.startRow; row <= bounds.endRow; row += 1) {
      const copiedRow: ChannelEvent[] = [];
      for (let channel = bounds.startChannel; channel <= bounds.endChannel; channel += 1) {
        copiedRow.push(cloneEvent(this.currentPattern().rows[row][channel]));
      }
      cells.push(copiedRow);
    }
    const clipboard = {
      width: bounds.endChannel - bounds.startChannel + 1,
      height: bounds.endRow - bounds.startRow + 1,
      cells
    };
    this.ui.clipboard = clipboard;
    this.emit();
    return clipboard;
  }

  pasteSelection(): void {
    if (this.ui.clipboard === null) {
      return;
    }
    this.mutateProject("Paste selection", () => {
      const startRow = this.ui.selection.focusRow;
      const startChannel = this.ui.selection.focusChannel;
      for (let rowOffset = 0; rowOffset < this.ui.clipboard!.height; rowOffset += 1) {
        const targetRow = startRow + rowOffset;
        if (targetRow >= TRACKER_ROWS_PER_PATTERN) {
          break;
        }
        for (let channelOffset = 0; channelOffset < this.ui.clipboard!.width; channelOffset += 1) {
          const targetChannel = startChannel + channelOffset;
          if (targetChannel >= TRACKER_CHANNEL_COUNT) {
            break;
          }
          this.currentPattern().rows[targetRow][targetChannel] = cloneEvent(this.ui.clipboard!.cells[rowOffset][channelOffset]);
        }
      }
    });
  }

  clearSelection(): void {
    this.mutateProject("Clear selection", () => {
      const bounds = selectionBounds(this.ui.selection);
      for (let row = bounds.startRow; row <= bounds.endRow; row += 1) {
        for (let channel = bounds.startChannel; channel <= bounds.endChannel; channel += 1) {
          this.currentPattern().rows[row][channel] = {
            note: TRACKER_NOTE_EMPTY,
            instrument: TRACKER_INSTRUMENT_EMPTY,
            effect: 0,
            effectArg: 0
          };
        }
      }
    });
  }

  transposeSelection(delta: number): void {
    this.mutateProject(delta < 0 ? "Transpose down" : "Transpose up", () => {
      const bounds = selectionBounds(this.ui.selection);
      for (let row = bounds.startRow; row <= bounds.endRow; row += 1) {
        for (let channel = bounds.startChannel; channel <= bounds.endChannel; channel += 1) {
          const event = this.currentPattern().rows[row][channel];
          if (event.note === TRACKER_NOTE_EMPTY || isTrackerControlNote(event.note)) {
            continue;
          }
          event.note = Math.max(1, Math.min(TRACKER_NOTE_COUNT, event.note + delta));
        }
      }
    });
  }

  insertRow(): void {
    this.mutateProject("Insert row", () => {
      const row = clampRow(this.ui.selection.focusRow);
      const pattern = this.currentPattern();
      pattern.rows.splice(row, 0, Array.from({ length: TRACKER_CHANNEL_COUNT }, () => ({ note: 0, instrument: 0xff, effect: 0, effectArg: 0 })));
      pattern.rows.length = TRACKER_ROWS_PER_PATTERN;
    });
  }

  deleteRow(): void {
    this.mutateProject("Delete row", () => {
      const row = clampRow(this.ui.selection.focusRow);
      const pattern = this.currentPattern();
      pattern.rows.splice(row, 1);
      pattern.rows.push(Array.from({ length: TRACKER_CHANNEL_COUNT }, () => ({ note: 0, instrument: 0xff, effect: 0, effectArg: 0 })));
    });
  }

  duplicateRow(): void {
    this.mutateProject("Duplicate row", () => {
      const row = clampRow(this.ui.selection.focusRow);
      const pattern = this.currentPattern();
      const source = pattern.rows[row].map((event) => cloneEvent(event));
      pattern.rows.splice(row + 1, 0, source);
      pattern.rows.length = TRACKER_ROWS_PER_PATTERN;
    });
  }

  undo(): void {
    const snapshot = this.undoStack.pop();
    if (snapshot === undefined) {
      return;
    }
    this.redoStack.push({
      label: snapshot.label,
      project: structuredCloneProject(this.project),
      ui: {
        activePattern: this.ui.activePattern,
        selectedOrderSlot: this.ui.selectedOrderSlot,
        selectedInstrument: this.ui.selectedInstrument,
        selection: { ...this.ui.selection }
      }
    });
    this.restoreSnapshot(snapshot);
    this.projectRevision += 1;
    this.emit();
  }

  redo(): void {
    const snapshot = this.redoStack.pop();
    if (snapshot === undefined) {
      return;
    }
    this.undoStack.push({
      label: snapshot.label,
      project: structuredCloneProject(this.project),
      ui: {
        activePattern: this.ui.activePattern,
        selectedOrderSlot: this.ui.selectedOrderSlot,
        selectedInstrument: this.ui.selectedInstrument,
        selection: { ...this.ui.selection }
      }
    });
    this.restoreSnapshot(snapshot);
    this.projectRevision += 1;
    this.emit();
  }

  updatePlayback(partial: Partial<TrackerPlaybackUi>): void {
    this.ui.playback = {
      ...this.ui.playback,
      ...partial
    };
    const shouldFollowPlayback = this.ui.followRow && this.ui.playback.state === "playing";
    if (shouldFollowPlayback && partial.pattern !== undefined) {
      this.ui.activePattern = sanitizePatternIndex(partial.pattern, this.project.song.patterns.length);
    }
    if (shouldFollowPlayback && partial.order !== undefined) {
      this.ui.selectedOrderSlot = clampOrderSlot(partial.order, this.project.song.orderList.length);
    }
    if (shouldFollowPlayback && partial.row !== undefined) {
      this.ui.selection = {
        anchorChannel: this.ui.selection.focusChannel,
        anchorRow: clampRow(partial.row),
        focusChannel: this.ui.selection.focusChannel,
        focusRow: clampRow(partial.row)
      };
    }
    this.emit();
  }
}

export function trackerSelectionBounds(selection: TrackerSelection) {
  return selectionBounds(selection);
}
